import { createMockableFunction } from 'app/utils/mock';
import makeBurstBlocker from 'app/utils/makeBurstBlocker';
import Logger from 'app/services/logger';
// Global error dispatching tool. Listens for error events that pass through window.

const listeners = [];

/**
 * Dispatches an error to all listeners.
 *
 * To avoid DDoS-ing honeybadger, the number of time notify can be called within
 * a minute is limited by a burst blocker. The goal is to prevent a harmful
 * number of errors from reaching honeybadger, without blocking so many that we
 * miss what might otherwise be important data.
 *
 * @param {Error} error      ideally should be an error, but will pass anything along
 * @param {Object} metaData  ideally should be an object, but will pass anything along
 */
export const notify = createMockableFunction((error, metaData) => {
  try {
    const isBlocked = notifyBurstBlocker.isBlocked();
    if (isBlocked) {
      Logger.warn('Throttled notify:', error, metaData);
      return;
    }
  } catch (e) {
    // This code is unlikely to ever throw an error, but if it does it will
    // prevent all other errors from being reported, so report the error if it
    // does throw.
    notifyListeners(e);
  }

  notifyListeners(error, metaData);
});

// Limit number of `notify` calls per minute, to prevent a client getting in a
// bad state from making us go over our monthly Honeybadger report allowance.
const notifyBurstBlocker = makeBurstBlocker({
  maxCalls: 30,
  perMs: 60 * 1000,
});

const notifyListeners = (error, metaData = {}) => {
  for (let i = 0; i < listeners.length; i++) {
    const handler = listeners[i];
    setTimeout(() => handler(error, metaData), 0);
  }
};

/**
 * Unbinds all listeners.
 * Should be used primarily for testing purposes.
 */
export const reset = () => {
  listeners.splice(0, listeners.length);
  notifyBurstBlocker.reset();
};

/**
 * Add a handler for when notify is called.
 * @param {Function} handler
 */
export const subscribe = (handler) => {
  if (listeners.indexOf(handler) === -1) {
    listeners.push(handler);
  }
};

/**
 * Removes the handler from listening for errors.
 * @param {Function} handler
 */
export const unsubscribe = (handler) => {
  const index = listeners.indexOf(handler);

  if (index !== -1) {
    listeners.splice(index, 1);
  }
};
